// src/routes/pages.tsx
//
// Central dispatch table: `{moduleKey}/{tab}` → React element.
//
// Replaces the 35+ conditional `activeTab === 'x' && <Component />` renders
// that used to live in App.tsx. Adding a new page = append a key here and
// register its sidebar item in `src/modules/registry.tsx`.
//
// Items declared `comingSoon: true` in the registry that have no entry in
// this map render `<ComingSoon />`. Unknown module/tab combinations render
// `<NotFound />`.

import { lazy, Suspense, type ReactNode } from 'react';

import { API_BASE_URL } from '@/config';
import { AutomationsPage } from '@/components/AutomationsPage';
import { AudienceBuilderPage } from '@/components/marketing/AudienceBuilderPage';
import { BoostFlow } from '@/components/marketing/BoostFlow';
import { CampaignDirectorPage } from '@/components/marketing/CampaignDirectorPage';
import { CampaignsPage } from '@/components/marketing/CampaignsPage';
import { ContentGeneratorPage } from '@/components/marketing/ContentGeneratorPage';
import { GalleryPage } from '@/modules/gallery/GalleryPage';
import { OptimizerAlertsPage } from '@/components/marketing/OptimizerAlertsPage';
import { SocialPublishCalendar } from '@/components/marketing/SocialPublishCalendar';
import { ApprovalQueue } from '@/components/marketing/ApprovalQueue';
import { BusinessAdmin } from '@/components/businesses/BusinessAdmin';
import { AIAgentConfigPanel } from '@/components/businesses/AIAgentConfig';
import { LeadImportPage, LeadLandingsView } from '@/modules/leads-import';
import { AVProducerPage } from '@/modules/av-producer';
import { VisitorsPage, CampaignResultsPage } from '@/modules/public-tracking';
import { GoogleAdsView, GoogleCuentaView } from '@/modules/google-app';
import { VentasView } from '@/modules/ventas';
import {
  MedicionView as MarketingMedicionView,
  PresupuestoView as MarketingPresupuestoView,
  ResumenView as MarketingResumenView,
} from '@/modules/marketing-os';
import { LeadMagnetsCreateView, LeadMagnetsListView } from '@/modules/lead-magnets';
import { LeadsView as PipelineLeadsView, PipelineInventoryView, PipelineRunsView } from '@/modules/pipeline-constructor';
import {
  DocumentsMineView,
  DocumentsSharedView,
  DocumentsOrgView,
  DocumentsUploadView,
} from '@/modules/documents';
import { ClinicalAgendaView, ClinicalHomePage, ClinicalPatientsView } from '@/modules/clinical-cockpit';
import { WebmailView } from '@/modules/webmail';
import { PerformanceView } from '@/modules/performance';
import { FocoBoard } from '@/modules/focus';
import { DiagFrame, EmpresasView as DiagEmpresasView, FunnelView as DiagFunnelView, ProgramsView as DiagProgramsView, ProjectsView as DiagProjectsView } from '@/modules/diagnostics';
import { LeadsView } from '@/modules/phone-leads';
import {
  ControlPanelView,
  PosCatalogoView,
  PosCierresView,
  PosPuntosView,
  PosVentasView,
} from '@/modules/pos';
import {
  InventoryExpiringView,
  InventoryRequestsView,
  InventoryStockView,
  InventoryTransfersView,
} from '@/modules/inventory';
import {
  AbsencesView,
  AttendanceView,
  ChecklistsView,
  IncidentsView,
  TasksView,
} from '@/modules/workforce';
import {
  ExpensesCreateView,
  ExpensesDashboardView,
  ExpensesListView,
} from '@/modules/expenses';
import { ClientPortalView } from '@/modules/clients-portal';
import { ObraRadarPage } from '@/modules/obra-radar';
import { ClientRadarPage } from '@/modules/client-radar';
import { MemoryGraphPage } from '@/modules/memory-graph';
import { ConsolidationPage } from '@/modules/consolidation';
import { WhatsAppConsolePage } from '@/modules/whatsapp-console';
import { WhatsAppApp } from '@/modules/whatsapp-app';
import { LeadsApp } from '@/modules/leads-app';
import { DealCreateView, DealForecastView, DealKanbanView } from '@/modules/deals';
import { BazaresPage } from '@/modules/bazares';
import { CitizenDashboard, CitizenInbox } from '@/modules/citizen-reports';
import { GovHealthApp } from '@/modules/gov-health';
import { InteligenciaPanel, PresidentBoard } from '@/modules/gov-executive';
import { GovernancePage } from '@/modules/governance';
import { PromptEditorPage } from '@/modules/agent-prompt';
import { SemaforoPage } from '@/modules/establishments';
import { TerritoryApp } from '@/modules/gov-territory';
// Orion for Government (C4 + CIBA): carga perezosa (Leaflet) — solo orgs que las encienden.
const GovSecurityApp = lazy(() => import('@/modules/gov-security/GovSecurityApp'));
const GovAnimalApp = lazy(() => import('@/modules/gov-animal/GovAnimalApp'));
import { BuzonMatrixPage } from '@/modules/buzon-matrix';
import {
  InvoicingCreateView, InvoicingDashboardView, InvoicingListView,
} from '@/modules/invoicing/routes';
import {
  QuotesCreateView, QuotesListView, QuotesTemplatesView,
} from '@/modules/quotes/routes';
import { AvailabilityPage, FleetBIPage, InventoryPage, RentalsPage } from '@/modules/rental-assets';
import { UniversityBIPage } from '@/modules/university-bi';
import { PlantelPage } from '@/modules/futuria-plantel';
import { ModuleAdminPage } from '@/modules/module-admin';
import { MarketingSafetyPanel } from '@/components/businesses/MarketingSafetyPanel';
import { NotificacionesPanel } from '@/components/businesses/NotificacionesPanel';
import CostosPage from '@/modules/costos/CostosPage';
import { CandidatesPage } from '@/components/CandidatesPage';
import { ContactsPage } from '@/components/ContactsPage';
import { ConversationHistory } from '@/components/ConversationHistory';
import CustomerDashboard from '@/components/CustomerDashboard';
import { Dashboard } from '@/components/Dashboard';
import DatosBacked from '@/components/DatosBacked';
import { DevTasks } from '@/components/DevTasks';
import { EnvioMasivo } from '@/components/EnvioMasivo';
// LeadList (bookings/tour leads) deprecated — su única ruta crm/leads
// ahora apunta a LeadsCrmView. Si una org necesita la vista vieja,
// re-importar y mapear como 'crm/bookings-leads'.
// import { LeadList } from '@/components/LeadList';
import { LlamadasHistory } from '@/components/LlamadasHistory';
import { LlamadasMasivas } from '@/components/LlamadasMasivas';
import { MailAdmin } from '@/components/MailAdmin';
import { MarketingAdmin } from '@/components/MarketingAdmin';
import { PromptAdmin } from '@/components/PromptAdmin';
import { UsageGuide } from '@/components/UsageGuide';
import { WorkflowsPage } from '@/components/WorkflowsPage';
import { RecruitmentDashboard } from '@/components/RecruitmentDashboard';
import { SiteBuilder } from '@/components/SiteBuilder';
import { TemplatePicker } from '@/components/TemplatePicker';
import ThreadsDesk from '@/components/ThreadsDesk';
import { TravelAdmin } from '@/components/TravelAdmin';
import { MultiOrgPage } from '@/components/multi_org/MultiOrgPage';
import { TwilioPanel } from '@/components/TwilioPanel';
import { VacanciesBoard } from '@/components/VacanciesBoard';
import { VerbalAnalysis } from '@/components/VerbalAnalysis';
import VoiceChat from '@/components/VoiceChat';
import { ChatPane } from '@/components/assistant/ChatPane';
import { ChatPage } from '@/components/assistant/ChatPage';
import { EntrevistaPage } from '@/components/orgscan/EntrevistaPage';
import { ModulosPage, OrionAgentsPage, OrionAssistantPage, OrionCalendarPage, OrionHome, SiteChangesPage, TicketsPage } from '@/modules/orion-shell';
import { AjustesApp } from '@/modules/ajustes/AjustesApp';
import { SiteEditorPage } from '@/modules/site-editor';
import { GoogleAdsCampaignDetailPage } from '@/components/google_ads/GoogleAdsCampaignDetailPage';
import { GoogleAdsCampaignsPage } from '@/components/google_ads/GoogleAdsCampaignsPage';
import { VideoGalleryPage } from '@/components/video/VideoGalleryPage';
import { VideoTemplatesPage } from '@/components/video/VideoTemplatesPage';
import { ChatbotFlowsPage } from '@/components/chatbot/ChatbotFlowsPage';
import { ChatbotSessionsPage } from '@/components/chatbot/ChatbotSessionsPage';
import { AnalyticsOverviewPage } from '@/components/analytics/AnalyticsOverviewPage';
import { KpiDashboardPage } from '@/components/analytics/KpiDashboardPage';
import { CampaignAlertsPage } from '@/components/analytics/CampaignAlertsPage';
import { HeyGenVideoPage } from '@/components/heygen/HeyGenVideoPage';
import { HeyGenStudioPage } from '@/components/heygen/HeyGenStudioPage';
import { HeyGenTemplatesPage } from '@/components/heygen/HeyGenTemplatesPage';
import { HeyGenTranslatePage } from '@/components/heygen/HeyGenTranslatePage';
import { HeyGenJobsPage } from '@/components/heygen/HeyGenJobsPage';
import { BookingsList as BookingsListAdmin } from '@/modules/bookings/components/BookingsList';
import { ProfilesList as BookingProfilesList } from '@/modules/bookings/components/ProfilesList';
import { TTSPage } from '@/components/elevenlabs/TTSPage';
import { PodcastPage } from '@/components/elevenlabs/PodcastPage';
import { SFXMusicPage } from '@/components/elevenlabs/SFXMusicPage';
import { ToolsPage } from '@/components/elevenlabs/ToolsPage';
import { ElevenLabsJobsPage } from '@/components/elevenlabs/ElevenLabsJobsPage';
import {
  ProjectsListView,
  ProjectsTimeEntriesView,
} from '@/modules/build-thoth-module-projects-v0';
import { useParams } from 'react-router-dom';
// ADR-004 · Sales Desk (panel de vendedores)
import { MisComisiones } from '@/modules/sales-desk/components/MisComisiones';
import { MisVentas } from '@/modules/sales-desk/components/MisVentas';
import { ReglasComision } from '@/modules/sales-desk/components/ReglasComision';
import { useModules } from '@/context/ModulesContext';

/** Shared props every page may need. Keeps the dispatcher signature
 *  stable so adding a new page doesn't force a refactor of the map. */
export interface PageProps {
  addNotification: (notification: any) => void;
  webChatHeaders: Record<string, string>;
  webChatPathBuilder: (id: string) => string;
}

type PageBuilder = (props: PageProps) => ReactNode;

// ── Helpers ──────────────────────────────────────────────────
const travel = (tab: string): PageBuilder =>
  (p) => <TravelAdmin addNotification={p.addNotification} initialTab={tab} />;

const marketing = (tab: string): PageBuilder =>
  (p) => <MarketingAdmin addNotification={p.addNotification} initialTab={tab} />;

// ── Sales Desk (ADR-004) ─────────────────────────────────────
// El vendedor ve solo lo suyo (lo fuerza el SERVIDOR); isAdmin solo cambia
// qué controles se pintan, nunca qué datos llegan.
const SalesComisiones = () => {
  const { isAdmin } = useModules();
  return <MisComisiones isAdmin={!!isAdmin} />;
};
const SalesVentas = () => {
  const { isAdmin } = useModules();
  return <MisVentas isAdmin={!!isAdmin} />;
};

// ── "Mi Correo" = producto único: webmail completo para TODAS las orgs con
// correo (Calaita, Galán…), con la capa de prioridad IA encima. Un solo
// producto MiCorreo.
const MiCorreoRouter = () => <WebmailView />;

// ── Dispatch map (keyed by "{moduleKey}/{tab}") ──────────────
const PAGES: Record<string, PageBuilder> = {
  // ═══ Sales Desk · panel de vendedores (ADR-004) ══════════
  // ═══ POS · mostrador ═══════════════════════════════════════
  // ═══ Inventario ════════════════════════════════════════════
  // ═══ Operación ═════════════════════════════════════════════
  // ═══ Gastos ════════════════════════════════════════════════
  // El módulo existía en el registry desde hace meses pero sus páginas
  // nunca se cablearon aquí: entrar daba NotFound.
  'expenses/list': () => <ExpensesListView />,
  'expenses/create': () => <ExpensesCreateView />,
  'expenses/dashboard': () => <ExpensesDashboardView />,

  'workforce/asistencia': () => <AttendanceView />,

  // ═══ Deals · app «Deals» del SO (pipeline, forecast, alta) ═══
  // Estaba en el registry pero sin página: entrar daba NotFound.
  'deals/kanban': () => <DealKanbanView />,
  'deals/forecast': () => <DealForecastView />,
  'deals/create': () => <DealCreateView />,
  'workforce/checklists': () => <ChecklistsView />,
  'workforce/incidencias': () => <IncidentsView />,
  'workforce/tareas': () => <TasksView />,
  'workforce/ausencias': () => <AbsencesView />,

  'inventory/existencias': () => <InventoryStockView />,
  'inventory/caducar': () => <InventoryExpiringView />,
  'inventory/traslados': () => <InventoryTransfersView />,
  'inventory/solicitudes': () => <InventoryRequestsView />,

  'pos/panel': () => <ControlPanelView />,
  'pos/ventas': () => <PosVentasView />,
  'pos/cierres': () => <PosCierresView />,
  'pos/catalogo': () => <PosCatalogoView />,
  'pos/puntos': () => <PosPuntosView />,

  'sales-desk/comisiones': () => <SalesComisiones />,
  'sales-desk/ventas': () => <SalesVentas />,
  'sales-desk/reglas': () => <ReglasComision />,

  // ═══ Travel / Sales ══════════════════════════════════════
  'travel-sales/dashboard': travel('dashboard'),
  'travel-sales/bookings': travel('bookings'),
  'travel-sales/trips': travel('trips'),
  'travel-sales/categories': travel('categories'),
  'travel-sales/plans': travel('plans'),
  'travel-sales/addons': travel('addons'),
  'travel-sales/blogs': travel('blogs'),
  'travel-sales/authors': travel('authors'),

  // ═══ Bookings 1:1 — coaches/personal-brand (NABU-71) ═════
  'bookings-1to1/list':     () => <BookingsListAdmin />,
  'bookings-1to1/profiles': () => <BookingProfilesList />,

  // ═══ Empresa (top-level, moved out of travel) ═════════════
  'empresa/company': travel('company'),
  'empresa/ai-agent': (p) => <AIAgentConfigPanel addNotification={p.addNotification as any} />,
  'costos/resumen': () => <CostosPage />,
  'empresa/notificaciones': (p) => <NotificacionesPanel addNotification={p.addNotification as any} />,
  'empresa/marketing-safety': (p) => <MarketingSafetyPanel addNotification={p.addNotification as any} />,
  'empresa/negocios': (p) => <BusinessAdmin addNotification={p.addNotification} />,
  // Configuración → Módulos: el admin prende/apaga los módulos de SU org (Orion /org/modules/catalog).
  'empresa/modulos': (p) => <ModulosPage addNotification={p.addNotification as any} />,

  // ═══ Leads · Importar (CSV → Orion mapping → bulk + auto presentación + landing) ══
  'leads-import/upload': (p) => <LeadImportPage addNotification={p.addNotification as any} />,
  // App «Leads» del SO (lista · embudo · ficha). LeadsCrmView sigue exportado en @/modules/leads-import.
  'leads-import/crm': (p) => <LeadsApp addNotification={p.addNotification as any} />,
  'leads-import/landings': (p) => <LeadLandingsView addNotification={p.addNotification as any} />,
  'pipeline-constructor/leads': () => <PipelineLeadsView />,
  'pipeline-constructor/inventory': () => <PipelineInventoryView />,
  'pipeline-constructor/runs': () => <PipelineRunsView />,

  // ═══ AV Producer — Orión productor audiovisual (drag-drop fotos → Kling) ══
  'av-producer/studio': (p) => <AVProducerPage addNotification={p.addNotification as any} />,

  // ═══ Public Tracking — KPIs del visitante público multi-tenant ══
  'public-tracking/visitors': (p) => <VisitorsPage addNotification={p.addNotification as any} />,
  'public-tracking/campana': (p) => <CampaignResultsPage addNotification={p.addNotification as any} />,

  // ═══ Multi-Org · god-mode (solo is_superuser=true, gated en registry) ══
  'multi-org/orgs': (p) => <MultiOrgPage addNotification={p.addNotification} />,


  // ═══ Google — Cuenta · Calendario · Google Ads ══
  'google/cuenta': () => <GoogleCuentaView />,
  // Misma página que /m/orion/calendario (#234): Google Calendar de la persona + agenda unificada.
  'google/calendario': () => <OrionCalendarPage />,
  'google/ads': (p) => <GoogleAdsView addNotification={p.addNotification as any} />,
  // ═══ Marketing (Marketing OS · F1) ══
  'marketing/resumen': () => <MarketingResumenView />,
  'marketing/presupuesto': (p) => <MarketingPresupuestoView addNotification={p.addNotification as any} />,
  'marketing/medicion': () => <MarketingMedicionView />,

  // ═══ Lead Magnets — las vistas existían pero no estaban en el mapa («Ruta no encontrada») ══
  'lead-magnets/list': () => <LeadMagnetsListView />,
  'lead-magnets/create': () => <LeadMagnetsCreateView />,

  // ═══ Marketing — Meta Ads (MarketingAdmin with initialTab) ══
  'mkt-meta/dashboard': marketing('dashboard'),
  'mkt-meta/leads': marketing('leads'),
  'mkt-meta/publish': marketing('publish'),
  // Track E2 — campaigns list + detail (detail reached via :subId nested route)
  'mkt-meta/campaigns': (p) => <CampaignsPage addNotification={p.addNotification} />,
  // CM-3c — agentic Campaign Director (ReAct loop + activity log polling)
  'mkt-meta/director': (p) => <CampaignDirectorPage addNotification={p.addNotification} />,
  // Track E2.4 — boost picker (page posts + one-shot boost create)
  'mkt-meta/boost': (p) => <BoostFlow addNotification={p.addNotification} />,
  // Track E3 — social publishing calendar + compose modal + media upload
  'mkt-meta/social-publish': (p) => <SocialPublishCalendar addNotification={p.addNotification} />,
  'mkt-meta/approve': (p) => <ApprovalQueue addNotification={p.addNotification as any} />,

  // ═══ Marketing — IA (Track E4) ═══════════════════════════════
  'mkt-ai/content':  (p) => <ContentGeneratorPage addNotification={p.addNotification} />,
  'mkt-ai/gallery-images': (p) => <GalleryPage addNotification={p.addNotification} />,
  'mkt-ai/audience': (p) => <AudienceBuilderPage addNotification={p.addNotification} />,
  'mkt-ai/alerts':   (p) => <OptimizerAlertsPage addNotification={p.addNotification} />,

  // WhatsApp (Track E5, /m/whatsapp/*) ya no es módulo suelto: vive dentro de la
  // app «WhatsApp» → Difusión. Las URLs viejas redirigen (ModuleRoute LEGACY_REDIRECTS).

  // ═══ Google Ads (Track E6) ═══════════════════════════════
  // La conexión vive ahora en la app «Google» (misma pantalla, diseño del OS).
  'mkt-google/dashboard': (p) => <GoogleAdsView addNotification={p.addNotification as any} />,
  'mkt-google/campaigns': (p) => <GoogleAdsCampaignsWithDetail addNotification={p.addNotification} />,

  // ═══ Video (Track E7) ═════════════════════════════════════
  'mkt-video/gallery':   (p) => <VideoGalleryPage addNotification={p.addNotification} />,
  'mkt-video/templates': (p) => <VideoTemplatesPage addNotification={p.addNotification} />,

  // ═══ Chatbot (Track E8) ═══════════════════════════════════
  'mkt-chatbot/flows':    (p) => <ChatbotFlowsPage addNotification={p.addNotification} />,
  'mkt-chatbot/sessions': (p) => <ChatbotSessionsPage addNotification={p.addNotification} />,

  // ═══ Analytics (F1 + CM-1 + CM-2a) ═══════════════════════
  'mkt-analytics/overview': (p) => <AnalyticsOverviewPage addNotification={p.addNotification} />,
  'mkt-analytics/kpis':     (p) => <KpiDashboardPage addNotification={p.addNotification} />,
  'mkt-analytics/alerts':   (p) => <CampaignAlertsPage addNotification={p.addNotification} />,

  // ═══ HeyGen — Video AI (Track E9) ═════════════════════════
  'heygen/video-generator': (p) => <HeyGenVideoPage addNotification={p.addNotification} />,
  'heygen/studio':          (p) => <HeyGenStudioPage addNotification={p.addNotification} />,
  'heygen/hg-templates':    (p) => <HeyGenTemplatesPage addNotification={p.addNotification} />,
  'heygen/translate':       (p) => <HeyGenTranslatePage addNotification={p.addNotification} />,
  'heygen/hg-jobs':         (p) => <HeyGenJobsPage addNotification={p.addNotification} />,

  // ═══ ElevenLabs — Audio AI (Track E10) ═════════════════════
  'elevenlabs/tts':       (p) => <TTSPage addNotification={p.addNotification} />,
  'elevenlabs/podcast':   (p) => <PodcastPage addNotification={p.addNotification} />,
  'elevenlabs/sfx-music': (p) => <SFXMusicPage addNotification={p.addNotification} />,
  'elevenlabs/tools':     (p) => <ToolsPage addNotification={p.addNotification} />,
  'elevenlabs/el-jobs':   (p) => <ElevenLabsJobsPage addNotification={p.addNotification} />,

  // ═══ Automations ═════════════════════════════════════════
  'automations/flows': (p) => <AutomationsPage addNotification={p.addNotification} />,

  // ═══ Site builder + theme ════════════════════════════════
  'site/editor': (p) => <SiteEditorPage webChatHeaders={p.webChatHeaders} variant="classic" />,
  'site/builder': (p) => <SiteBuilder addNotification={p.addNotification} />,
  'site/theme': (p) => <TemplatePicker addNotification={p.addNotification} />,

  // ═══ Mail ═════════════════════════════════════════════════
  'mail/mailboxes': (p) => <MailAdmin addNotification={p.addNotification} />,

  // ═══ Recruitment ══════════════════════════════════════════
  'recruitment/dashboard': (p) => <RecruitmentDashboard addNotification={p.addNotification} />,
  'recruitment/vacancies': () => <VacanciesBoard />,
  'recruitment/candidates': () => <CandidatesPage />,

  // ═══ Voice ════════════════════════════════════════════════
  'voice/voice': () => <VoiceChat />,
  'voice/calls': () => <LlamadasMasivas />,
  'voice/call-history': () => <LlamadasHistory />,

  // ═══ CRM ═════════════════════════════════════════════════
  // 'crm/leads': vista nueva sales.Lead CRM (Phase 2 cuatrillones).
  // Reemplaza la vieja <LeadList /> (bookings/tour leads) — esa view
  // queda sin ruta. Si alguna org la necesita, restaurar como
  // 'crm/bookings-leads' apuntando a LeadList.
  'crm/leads': (p) => <LeadsApp addNotification={p.addNotification as any} />,
  'crm/contacts': () => <ContactsPage />,
  'crm/bulk': () => <EnvioMasivo />,
  'crm/history': () => <ConversationHistory />,

  // ═══ Dev-only ═════════════════════════════════════════════
  'dev/backend': () => <DatosBacked />,
  'dev/dev-tasks': () => <DevTasks />,
  'dev/my-threads': () => (
    <div className="h-[calc(100vh-140px)]">
      <ThreadsDesk apiBase={API_BASE_URL.replace(/\/$/, '')} mode="personal" />
    </div>
  ),
  'dev/customers': () => <CustomerDashboard />,
  'dev/twilio': () => <TwilioPanel />,

  // ═══ Config ═══════════════════════════════════════════════
  'config/guide': () => <UsageGuide />,
  'config/workflows': () => <WorkflowsPage />,
  'config/prompts': () => <PromptAdmin />,

  // ═══ Projects (NABU-62) ═══════════════════════════════════
  'projects/list': () => <ProjectsListView />,
  'projects/time-entries': () => <ProjectsTimeEntriesView />,

  // ═══ Documentos — Drive multi-tenant (TKT-144 Ola 1) ═══════
  // Personal / org / shared / public. Backend en
  // mcp_django/apps/documents/ con permission resolver STRICT.
  'documents/mine':   () => <DocumentsMineView />,
  'documents/shared': () => <DocumentsSharedView />,
  'documents/org':    () => <DocumentsOrgView />,
  'documents/upload': () => <DocumentsUploadView />,

  // ═══ Cockpit clínico — MEDOS-16 ════════════════════════════
  // Agenda del médico + lista de pacientes + drawer expediente.
  // Visible solo cuando archetype org = healthcare-clinic (futuro
  // MEDOS-14), por ahora visible para clinic-demo via registry.
  'clinical-cockpit/home':     () => <ClinicalHomePage />,
  'clinical-cockpit/agenda':   () => <ClinicalAgendaView />,
  'clinical-cockpit/patients': () => <ClinicalPatientsView />,

  // ═══ Correo: webmail completo (agencia) o triage IA (Galán) ═══
  'galan-inbox/inbox':         () => <MiCorreoRouter />,
  'performance/tablero':       () => <PerformanceView />,
  'focus/tablero':             () => <FocoBoard />,
  // ADR-007: todas las pantallas dentro del marco con selector «Empresa» + banner.
  'diagnosticos/programas':    () => <DiagFrame ownOrgOnly><DiagProgramsView /></DiagFrame>,
  'diagnosticos/proyectos':    () => <DiagFrame><DiagProjectsView /></DiagFrame>,
  'diagnosticos/empresas':     () => <DiagFrame><DiagEmpresasView /></DiagFrame>,
  'diagnosticos/embudo':       () => <DiagFrame ownOrgOnly><DiagFunnelView /></DiagFrame>,
  'phone-leads/leads':         () => <LeadsView />,

  // Portal del Cliente (audience 'client') — una vista, 5 secciones por tab.
  'clients-portal/home':       () => <ClientPortalView tab="home" />,
  'clients-portal/cuenta':     () => <ClientPortalView tab="cuenta" />,
  'clients-portal/bono':       () => <ClientPortalView tab="bono" />,
  'clients-portal/documentos': () => <ClientPortalView tab="documentos" />,
  'clients-portal/contacto':   () => <ClientPortalView tab="contacto" />,

  // ═══ Obra Radar — demanda constructiva nacional (rentalmaq, service-pro) ═══
  'obra-radar/nacional':       () => <ObraRadarPage />,
  'client-radar/leads':        () => <ClientRadarPage />,
  'memory-graph/graph':        () => <MemoryGraphPage />,
  'consolidation/review':      () => <ConsolidationPage />,
  'whatsapp-console/console':   () => <WhatsAppConsolePage />,
  'bazares/ventas':      () => <BazaresPage tab="ventas" />,
  'bazares/stands':      () => <BazaresPage tab="stands" />,
  'bazares/expositores': () => <BazaresPage tab="expositores" />,
  'bazares/cobrar':      () => <BazaresPage tab="cobrar" />,
  'bazares/material':    () => <BazaresPage tab="material" />,
  'citizen-reports/buzones': () => <CitizenInbox />,
  'citizen-reports/dashboard': () => <CitizenDashboard />,
  'gov-health/panorama': () => <GovHealthApp view="panorama" />,
  'gov-health/jornada': () => <GovHealthApp view="jornada" />,
  'gov-health/ruta': () => <GovHealthApp view="ruta" />,
  'gov-health/senales': () => <GovHealthApp view="senales" />,
  'gov-health/canalizaciones': () => <GovHealthApp view="canalizaciones" />,
  'gov-health/asistente': () => <GovHealthApp view="asistente" />,
  'gov-executive/tablero': () => <PresidentBoard />,
  'gov-executive/inteligencia': () => <InteligenciaPanel />,
  'governance/contrato': () => <GovernancePage />,
  'agent-prompt/editor': () => <PromptEditorPage />,
  'establishments/semaforo': () => <SemaforoPage />,
  // Desarrollo Urbano · Territorio (visión satelital, demo B2G).
  'gov-territory/vegetacion': () => <TerritoryApp tab="vegetacion" />,
  'gov-territory/arbolado':   () => <TerritoryApp tab="arbolado" />,
  'gov-territory/metodo':     () => <TerritoryApp tab="metodo" />,
  'gov-security/consola': () => <Suspense fallback={null}><GovSecurityApp /></Suspense>,
  'gov-animal/ciba': () => <Suspense fallback={null}><GovAnimalApp /></Suspense>,
  'buzon-matrix/matriz': () => <BuzonMatrixPage />,
  'conversaciones/todas': () => <ThreadsDesk apiBase={API_BASE_URL.replace(/\/$/, '')} mode="admin" />,
  'whatsapp-app/chats':    (p) => <WhatsAppApp view="chats" addNotification={p.addNotification} />,
  'whatsapp-app/difusion': (p) => <WhatsAppApp view="difusion" addNotification={p.addNotification} />,
  'whatsapp-app/numeros':  (p) => <WhatsAppApp view="numeros" addNotification={p.addNotification} />,
  'whatsapp-app/ajustes':  (p) => <WhatsAppApp view="ajustes" addNotification={p.addNotification} />,
  'university-bi/panel':       () => <UniversityBIPage />,
  'futuria-plantel/panel':    () => <PlantelPage />,

  // ═══ Inventario y Rentas — ERP núcleo (rentalmaq) ═══
  'rental-assets/inventario':  () => <InventoryPage />,
  'rental-assets/rentas':      () => <RentalsPage />,
  'rental-assets/disponibilidad': () => <AvailabilityPage />,
  'rental-assets/bi':          () => <FleetBIPage />,

  // ═══ Cotizaciones — el precio se NEGOCIA por trato (no hay lista fija),
  // así que la cotización es la fuente de verdad del precio que después
  // hereda la renta y termina en la factura. El módulo ya existía completo
  // (QuoteList/QuoteForm/QuoteDetail) pero sin registrar aquí: el menú lo
  // mostraba y al entrar daba "Ruta no encontrada".
  'quotes/list':               () => <QuotesListView />,
  'quotes/create':             () => <QuotesCreateView />,
  'quotes/templates':          () => <QuotesTemplatesView />,

  // ═══ Facturación — cierra renta → cobro → factura → pago. Mismo caso:
  // apps/invoicing (back) y modules/invoicing (front) ya existían; faltaba
  // el cable. Con esto "Facturación" deja de dar 404.
  'invoicing/dashboard':       () => <InvoicingDashboardView />,
  'invoicing/list':            () => <InvoicingListView />,
  'invoicing/create':          () => <InvoicingCreateView />,

  // ═══ Ventas (TKT-00223) — cobros de Stripe de la org (eventos, reservas,
  // expositores) con factura pendiente visible. Backend: apps/invoicing/ventas.py.
  'ventas/cobros':             () => <VentasView />,

  // ═══ Admin de Módulos — God-mode DB-driven (ucognet) ═══
  'module-admin/matrix':       () => <ModuleAdminPage />,

  // ═══ Shell «Orion» (opt-in por datos; módulo con requireShell) ═══
  'orion/inicio':        (p) => <OrionHome webChatHeaders={p.webChatHeaders} />,
  'orion/asistente':     (p) => <OrionAssistantPage webChatHeaders={p.webChatHeaders} />,
  'orion/agentes':       () => <OrionAgentsPage />,
  // Agentes y Configuración: la MISMA app en ambos shells (ver registry.tsx).
  'agentes/mis-agentes': () => <OrionAgentsPage />,
  'ajustes/inicio':      (p) => <AjustesApp addNotification={p.addNotification as any} />,
  'orion/calendario':    () => <OrionCalendarPage />,
  'orion/cambios-sitio': () => <SiteChangesPage />,
  'orion/tickets':       () => <TicketsPage />,
  'orion/editor-sitio':  (p) => <SiteEditorPage webChatHeaders={p.webChatHeaders} />,
};

// Wrapper: /m/mkt-google/campaigns        → list
//          /m/mkt-google/campaigns/:subId → detail
// Lives here so pages.tsx stays a flat (moduleKey/tab) dispatcher while
// still supporting campaign drill-in via the :subId route segment.
function GoogleAdsCampaignsWithDetail({ addNotification }: PageProps) {
  const { subId } = useParams();
  return subId
    ? <GoogleAdsCampaignDetailPage addNotification={addNotification} />
    : <GoogleAdsCampaignsPage addNotification={addNotification} />;
}

// ── Extra page builders reachable by URL but not in the sidebar
// ── (kept for backward-compat with old deep-links).
const EXTRAS: Record<string, PageBuilder> = {
  'travel-sales/overview': (p) => <Dashboard addNotification={p.addNotification} />,
  'dev/verbal': () => <VerbalAnalysis />,
  // Interfaz ChatGPT-like (slice 1). Reachable en /m/assistant/chat sin tocar
  // el sidebar/gating de ninguna org. Wiring al nav de ucognet = paso posterior.
  'assistant/chat': (p) => <ChatPage webChatHeaders={p.webChatHeaders} />,
  'org-scan/entrevista': (p) => <EntrevistaPage webChatHeaders={p.webChatHeaders} />,
  'dev/assistant': (p) => (
    <div className="h-[calc(100vh-140px)]">
      {/* Mismo chat que /chat (antes WebChat): stream, @ / #, modelos, visuales, adjuntos. */}
      <ChatPane webChatHeaders={p.webChatHeaders} modelPicker mentions />
    </div>
  ),
};

/** Look up a page renderer by moduleKey + tab. `null` if no match. */
export function resolvePage(
  moduleKey: string,
  tab: string,
  props: PageProps,
): ReactNode | null {
  const key = `${moduleKey}/${tab}`;
  const builder = PAGES[key] ?? EXTRAS[key];
  return builder ? builder(props) : null;
}

/** Used by ModuleRoute to tell "page missing" (404) apart from "page
 *  intentionally not built yet" (coming-soon placeholder). */
export function hasConcretePage(moduleKey: string, tab: string): boolean {
  const key = `${moduleKey}/${tab}`;
  return key in PAGES || key in EXTRAS;
}
